/**
 * Route guard for `/courses/notes` — the Notes Hub — on top of the Courses gate.
 *
 * Decision 7 of the encryption spec (section 6.10): production web does not offer
 * notes, so on a web build the Hub route redirects to the courses home unless the
 * test-only `courses-notes-web` flag is on for the member. A native build never
 * consults the flag and behaves exactly as the plain Courses gate did.
 *
 * ORDER. The web check runs FIRST. Both outcomes for a web visitor with the flag
 * off end at a redirect, so running the pastoral and access lookups inside
 * coursesGuard beforehand would spend three API calls deciding something the
 * platform already settled. The redirect target then runs coursesGuard itself.
 *
 * THE FLAG IS LOADED, NOT READ. Route guards run inside router.isReady(), before
 * the flags fetched on boot have landed, so on a cold deep link the in-memory Map
 * is still empty. `requireWebFlag` is the router's requireFeatureFlag bound to the
 * flag and the fallback path: it loads app-flags, then user-flags, and only then
 * answers. Reading `isEnabled` here directly would send every cold web open of the
 * Hub home, flag or no flag.
 *
 * Dependencies are injected so the three branches can be asserted without
 * importing the router (and with it every page component in the app). Returns
 * ONE function, not an array, for the reason courses-guard.js gives: the auth-guard
 * composition in router/index.js wraps `beforeEnter` as `[authGuard, existing]`.
 *
 * @param {Object} deps
 * @param {() => boolean} deps.isNative - Capacitor.isNativePlatform(), behind a seam
 * @param {() => Promise<string|undefined>} deps.requireWebFlag - resolves to the
 *   fallback path when the web flag is off, undefined when on
 * @param {(to: Object) => Promise<string|undefined>} deps.coursesGuard - the composed
 *   Courses gate (token, `courses` flag, pastoral status)
 * @returns {(to: Object) => Promise<string|undefined>} a Vue Router beforeEnter guard
 */
export function createCoursesNotesWebGuard({ isNative, requireWebFlag, coursesGuard }) {
  return async to => {
    if (!isNative()) {
      const webResult = await requireWebFlag();
      if (webResult) return webResult;
    }
    return coursesGuard(to);
  };
}
